import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import { Footer, Modal } from 'tio-ui/components/modal';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import RemoteSelect from 'tio-ui/components/remote-select';
import { task } from 'reactiveweb/ember-concurrency';
import type { ApplicationApprovalSummaryApprover } from '../../../types/tuition-assistance.ts';
import type EmployeeModel from '../../../models/employee.ts';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type TasProgramInstance from '../../../models/tas-program-instance.ts';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
import type { NotificationsService } from 'tio-ui/components/notifications';

const INPUT_DEBOUNCE = /* config.environment === 'test' ? 0 : */ 250;

interface ReassignModalSignature {
  show: boolean;
  onClose: () => void;
  approverToReassign: ApplicationApprovalSummaryApprover;
  application: TasApplicationModel | TasProgramInstance;
  companyId: string;
}

export default class ReassignModalComponent extends Component<ReassignModalSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;

  @tracked selectedApproverId = '';
  @tracked searchTerm = '';

  onFilterChange = (event: Event) => {
    this.searchTerm = (event.target as HTMLInputElement).value;
  };

  handleSelectionChange = async (key: string | null) => {
    this.selectedApproverId = key || '';
  };

  insertReviewer = dropTask(async () => {
    if (!this.selectedApproverId) return;
    const adapter =
      this.args.application.modelName === 'tas-application'
        ? this.store.adapterFor('tas-application')
        : this.store.adapterFor('tas-program-instance');

    return adapter.insertReviewer(
      this.args.application,
      this.selectedApproverId,
      this.args.approverToReassign.ordinal
    );
  });

  searchApprovers = restartableTask(async (search: string) => {
    if (!search) {
      return [];
    }
    await timeout(INPUT_DEBOUNCE);
    const results = await this.store.query<EmployeeModel>('employee', {
      filter: { company_id: this.args.companyId, search },
    });
    return results.map((employee) => ({
      key: employee.id!,
      label: employee.fullName || employee.id!,
    }));
  });

  fetchApproversTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.searchApprovers,
    () => [this.searchTerm]
  );

  get items() {
    return this.fetchApproversTask.value ?? [];
  }

  onClose = () => {
    this.selectedApproverId = '';
    this.searchTerm = '';
    this.args.onClose();
  };

  submit = async () => {
    if (!this.selectedApproverId) return;

    try {
      await this.insertReviewer.perform();
      this.notifications.add('Successfully reassigned approver', {
        appearance: 'success',
      });
      this.onClose();
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  };

  <template>
    <Modal @isOpen={{@show}} @onClose={{this.onClose}} class="min-h-64" @backdrop="faded" as |m|>
      <m.Header>{{t "tuition_assistance.approval_queue.reassign"}}</m.Header>
      <m.Body>
        <div class="flex flex-col gap-y-2">
          <RemoteSelect
            data-test-approver-select
            @label={{t "tuition_assistance.approval_queue.select_replacement_approver"}}
            @isFilterable={{true}}
            @isLoading={{this.fetchApproversTask.isRunning}}
            @selectedKey={{this.selectedApproverId}}
            @items={{this.items}}
            {{! @glint-expect-error}}
            @onFilterChange={{this.onFilterChange}}
            @onSelectionChange={{this.handleSelectionChange}}
            @isClearable={{true}}
          >
            <:item as |o|>
              <o.Item @key={{o.key}}>
                {{o.label}}
              </o.Item>
            </:item>
          </RemoteSelect>
        </div>
      </m.Body>
      <m.Footer>
        <Footer
          @isRunning={{this.insertReviewer.isRunning}}
          @onCancel={{this.onClose}}
          @onSubmit={{this.submit}}
        />
      </m.Footer>
    </Modal>
  </template>
}
